import { Link } from "react-router-dom";
import { ArrowRight, Home as HomeIcon, Church, HeartHandshake, Sparkles, TreePine, Flag } from "lucide-react";
import Seo from "@/components/Seo";
import { PageHeader } from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";

const CategoryCard = ({ id, num, title, icon: Icon, children, delay, featured = false }) => (
  <Reveal delay={delay} className={featured ? "sm:col-span-2" : ""}>
    <article
      data-testid={`performance-category-${id}`}
      className={`group flex h-full flex-col border p-8 transition-colors duration-300 sm:p-10 ${
        featured
          ? "border-gold/30 bg-charcoal-900/80 hover:border-gold/50"
          : "border-white/[0.08] bg-charcoal-900/60 hover:border-gold/40"
      }`}
    >
      <div className="flex items-center justify-between">
        <Icon size={22} className="text-gold" strokeWidth={1.5} />
        <span className="font-serif text-3xl font-light text-outline">{num}</span>
      </div>
      <h2 className={`mt-8 font-serif font-light text-sand-bright ${featured ? "text-2xl sm:text-3xl" : "text-2xl"}`}>
        {title}
      </h2>
      <div className="mt-4 space-y-4 text-sm sm:text-base font-light leading-relaxed text-mist">
        {children}
      </div>
    </article>
  </Reveal>
);

export default function Performances() {
  return (
    <>
      <Seo
        title="Live Performances — Derrick Hall, Christian Singer"
        siteName="Derrick Hall"
        description="Derrick Hall brings Christian and inspirational music to at-home concerts, churches and faith gatherings, senior living communities, weddings, anniversaries, and social gatherings."
      />
      <PageHeader
        testId="performances-header"
        eyebrow="Bringing the Concert to You"
        title="Where Derrick Sings"
        description="Christian and inspirational music, brought to the places people already gather. Find the setting that fits your occasion — or write with something entirely your own."
      />

      <div className="mx-auto max-w-7xl px-6 pb-28">
        <Reveal>
          <div className="mb-12 border-l-2 border-gold/50 bg-charcoal-900/60 px-6 py-5 sm:px-8" data-testid="performance-format-note">
            <p className="text-sm sm:text-base font-light leading-relaxed text-sand/90">
              Derrick performs live vocals with professionally produced instrumental
              accompaniment, creating a full concert experience that can be brought into
              intimate and flexible settings.
            </p>
          </div>
        </Reveal>
        <div className="grid gap-6 sm:grid-cols-2" data-testid="performance-categories">
          <CategoryCard id="home" num="01" title="At-Home Concerts" icon={HomeIcon} delay={0}>
            <p>
              Intimate Christian music experiences for family and friends in living
              rooms, backyards, patios, or other comfortable home settings.
            </p>
          </CategoryCard>

          <CategoryCard id="church" num="02" title="Churches & Faith Gatherings" icon={Church} delay={0.06}>
            <p>
              Christian and inspirational music for churches, fellowship gatherings,
              special programs, and faith-centered events.
            </p>
          </CategoryCard>

          <CategoryCard id="senior" num="03" title="Senior Living Communities" icon={HeartHandshake} delay={0.12} featured>
            <p>
              Derrick Hall provides warm, engaging Christian and inspirational music
              experiences for senior living communities, assisted living settings, and
              residential care environments.
            </p>
            <p>
              Programs can be designed for small gatherings, activity-room performances,
              special events, holiday programs, faith-centered gatherings, or simply an
              afternoon of music and encouragement.
            </p>
            <p>
              The emphasis is on connection — creating a comfortable atmosphere where
              residents can listen, sing along, reflect, and enjoy meaningful time
              together.
            </p>
            <div className="pt-2">
              <Link
                to="/booking"
                data-testid="senior-living-cta"
                className="group inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-gold"
              >
                Book a Senior Living Program
                <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </CategoryCard>

          <CategoryCard id="special" num="04" title="Weddings, Anniversaries & Social Gatherings" icon={Sparkles} delay={0.18} featured>
            <p>
              Derrick Hall brings warm, Christian and inspirational music to meaningful
              celebrations with family and friends. Performances can be tailored for
              weddings, anniversaries, vow renewals, receptions, birthdays, family
              gatherings, and other social occasions where live music can create a
              personal and memorable atmosphere.
            </p>
            <div className="pt-2">
              <Link
                to="/booking"
                data-testid="special-gathering-cta"
                className="group inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-gold"
              >
                Invite Derrick to Your Gathering
                <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </CategoryCard>

          <CategoryCard id="christmas" num="05" title="Christmas Concerts" icon={TreePine} delay={0.24} featured>
            <p>
              Celebrate the Christmas season through an intimate program of Christian
              and inspirational music. Derrick Hall brings Christmas music to churches,
              senior living communities, homes, family gatherings, and other meaningful
              seasonal celebrations — creating a warm setting for reflection, joy,
              faith, and connection.
            </p>
            <div className="pt-2">
              <Link
                to="/booking"
                data-testid="christmas-concert-cta"
                className="group inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-gold"
              >
                Book a Christmas Program
                <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </CategoryCard>

          <CategoryCard id="patriotic" num="06" title="Patriotic & Remembrance Programs" icon={Flag} delay={0.3} featured>
            <p>
              Derrick offers live vocal programs featuring patriotic and inspirational
              music for Veterans Day, Memorial Day, 9/11 remembrance events, senior
              living communities, churches, civic gatherings, and other meaningful
              occasions. Programs can be tailored to honor service, sacrifice,
              remembrance, and national unity in a respectful and uplifting setting.
            </p>
            <div className="pt-2">
              <Link
                to="/booking"
                data-testid="patriotic-program-cta"
                className="group inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-gold"
              >
                Request a Patriotic Program
                <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </CategoryCard>
        </div>

        <Reveal delay={0.15}>
          <div className="mt-16 flex flex-col items-start justify-between gap-8 border border-gold/25 bg-charcoal-900/60 p-8 sm:flex-row sm:items-center sm:p-12">
            <div>
              <h2 className="font-serif text-2xl sm:text-3xl font-light text-sand-bright">
                Planning a gathering?
              </h2>
              <p className="mt-3 max-w-lg text-sm sm:text-base font-light leading-relaxed text-mist">
                Send the details and Derrick will respond personally to talk through the
                occasion, the setting, and the music that would serve it best.
              </p>
            </div>
            <Link
              to="/booking"
              data-testid="performances-booking-cta"
              className="group flex shrink-0 items-center gap-2 bg-gold px-7 py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-charcoal-950 transition-colors duration-300 hover:bg-gold-light"
            >
              Bring the Concert to You
              <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
      </div>
    </>
  );
}
